import React, { useEffect, useMemo } from "react";
import Select from "react-select";
import { NumericFormat } from "react-number-format";
import { format, formatDistanceToNow } from "date-fns";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";


function SkeletonLine({ w = "100%", h = 14, className = "" }) {
  return (
    <div
      className={`ss-skeleton ${className}`}
      style={{ width: w, height: h, borderRadius: 10 }}
    />
  );
}

function SkeletonBlock({ h = 38, className = "" }) {
  return (
    <div
      className={`ss-skeleton ${className}`}
      style={{ width: "100%", height: h, borderRadius: 12 }}
    />
  );
}

function DrawerSkeleton({ mode }) {
    const title =
      mode === "create"
        ? "Preparing form..."
        : mode === "repeat"
        ? "Loading repeat sale..."
        : "Loading lead details...";
  
    // ✅ repeat skeleton lain sikit
    if (mode === "repeat") {
      return (
        <div className="p-2">
          <div className="mb-3">
            <SkeletonLine w="45%" h={18} className="mb-2" />
            <div className="small text-muted">{title}</div>
          </div>
  
          {/* tabs skeleton */}
          <div className="d-flex gap-2 mb-3">
            <div className="ss-skeleton" style={{ width: 120, height: 34, borderRadius: 999 }} />
            <div className="ss-skeleton" style={{ width: 120, height: 34, borderRadius: 999 }} />
          </div>
  
          {/* amount + remark skeleton */}
          <div className="mb-3">
            <SkeletonLine w="30%" h={10} className="mb-2" />
            <SkeletonBlock h={52} className="mb-3" />
            <SkeletonLine w="35%" h={10} className="mb-2" />
            <SkeletonBlock h={110} className="mb-3" />
          </div>
  
          {/* buttons skeleton */}
          <div className="d-grid gap-2 mb-4">
            <SkeletonBlock h={44} />
            <SkeletonBlock h={44} />
          </div>
  
          {/* history preview skeleton */}
          <SkeletonLine w="40%" h={10} className="mb-2" />
          <div className="border rounded-3 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="d-flex gap-2 py-2 align-items-center border-bottom">
                <SkeletonLine w="10%" h={10} />
                <SkeletonLine w="25%" h={10} />
                <SkeletonLine w="40%" h={10} />
                <SkeletonLine w="20%" h={10} />
              </div>
            ))}
          </div>
        </div>
      );
    }
  
    // ✅ default skeleton (create/edit)
    return (
      <div className="p-2">
        <div className="mb-3">
          <SkeletonLine w="55%" h={18} className="mb-2" />
          <div className="small text-muted">{title}</div>
        </div>
  
        <div className="mb-3">
          <SkeletonBlock h={52} className="mb-3" />
          <SkeletonBlock h={52} className="mb-3" />
          <SkeletonBlock h={52} className="mb-3" />
          <SkeletonBlock h={110} className="mb-3" />
          <SkeletonBlock h={52} className="mb-3" />
        </div>
  
        <div className="d-grid gap-2">
          <SkeletonBlock h={44} />
          <SkeletonBlock h={44} />
        </div>
      </div>
    );
  }

function DateTimePicker({
    value,
    onChange,
    placeholder = "Select date & time",
    minDate = new Date(),
  }) {
    return (
      <Flatpickr
        className="form-control"
        value={value || null} // ✅ pass Date object / null
        options={{
          enableTime: true,
          time_24hr: false,         // ✅ 12h mode
          altInput: true,           // ✅ show pretty input
          altFormat: "d M Y, h:i K",// ✅ 12h display e.g. 30 Jan 2026, 3:15 PM
          dateFormat: "Z",          // internal (timestamp) - stable
          minDate,
        }}
        onChange={(dates) => onChange(dates?.[0] || null)}
        placeholder={placeholder}
      />
    );
  }
  
  
  

export default function LeadDrawer({
  open,
  mode, // 'create' | 'edit' | 'repeat'
  onClose,
  teamId,
  template,

  countryOptions,
  filterOption,

  // LOADING
  isLoading = false,
  loadingText,

  // CREATE
  createName,
  setCreateName,
  createCountry,
  setCreateCountry,
  createMobile,
  setCreateMobile,
  createStatus,
  setCreateStatus,
  createRemarks,
  setCreateRemarks,
  createFollowUp,
  setCreateFollowUp,
  createClosedAmount,
  setCreateClosedAmount,
  onCreateSubmit,
  onCreateCountryChange,
  onCreatePhoneChange,

  // EDIT
  editId,
  editName,
  setEditName,
  editCountry,
  setEditCountry,
  editMobile,
  setEditMobile,
  editStatus,
  setEditStatus,
  editRemarks,
  setEditRemarks,
  editFollowUp,
  setEditFollowUp,
  editClosedAmount,
  setEditClosedAmount,
  editAddedDate,
  onEditPhoneChange,
  onEditSubmit,

  // REPEAT
  repeatSalesAmt,
  setRepeatSalesAmt,
  repeatRemark,
  setRepeatRemark,
  activeRepeatTab,
  setActiveRepeatTab,
  onRepeatSubmit,

  salesHistory,
  setSalesHistory,
  editingRowId,
  setEditingRowId,
  editRowAmount,
  setEditRowAmount,
  editRowRemarks,
  setEditRowRemarks,
  onSaveRepeatHistoryRow,

  initialClosedAmount,
  totalClosedValue,
}) {
  useEffect(() => {
    if (open) document.body.classList.add("offcanvas-open");
    else document.body.classList.remove("offcanvas-open");
    return () => document.body.classList.remove("offcanvas-open");
  }, [open]);

  const title = useMemo(() => {
    if (mode === "create") return "New Lead";
    if (mode === "repeat") return "Repeat Sale";
    return "Edit Lead";
  }, [mode]);

  const isRepeat = mode === "repeat";
  const isCreate = mode === "create";
  const isEdit = mode === "edit";

  const safeEditCountry = editCountry || null;

  const handleFollowUpChange = (dateObj, type) => {
    const iso = dateObj ? dateObj.toISOString() : "";
  
    if (type === "create") {
      setCreateFollowUp(iso);
      if (createStatus !== "Closed") setCreateStatus("Follow Up");
    } else {
      setEditFollowUp(iso);
      if (editStatus !== "Closed") setEditStatus("Follow Up");
    }
  };
  
  

  const onBackdrop = (e) => {
    if (e.target.classList.contains("offcanvas-backdrop-custom")) onClose();
  };

  // kalau drawer open tapi sedang load, elakkan user tutup sebab tersalah click — optional
  const allowClose = !isLoading;

  const editFollowUpDateObj = editFollowUp ? new Date(editFollowUp) : null;
    const isFutureFollowUp = editFollowUpDateObj && editFollowUpDateObj > new Date();


  return (
    <>
      {/* BACKDROP */}
      <div
        className={`offcanvas-backdrop-custom ${open ? "show" : ""}`}
        onMouseDown={allowClose ? onBackdrop : undefined}
      />

      {/* DRAWER */}
      <div
        className={`offcanvas-saas ${open ? "show" : ""} p-3`}
        role="dialog"
        aria-modal="true"
      >
        <div className="offcanvas-header border-bottom py-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="m-0">{title}</h5>

            {isLoading && (
              <span className="badge text-bg-secondary ss-badge-loading">
                <span className="spinner-border spinner-border-sm me-2" />
                {loadingText || "Loading"}
              </span>
            )}
          </div>

          <button
            className="btn btn-sm btn-light"
            onClick={onClose}
            disabled={!allowClose}
            title={!allowClose ? "Loading..." : "Close"}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="offcanvas-body p-3">
          {/* ✅ SKELETON STATE */}
          {isLoading ? (
            <DrawerSkeleton mode={mode} />
          ) : (
            <>
              {/* CREATE */}
              {isCreate && (
                <form onSubmit={onCreateSubmit}>
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                      required
                    />
                    <label>Name</label>
                  </div>

                  <div className="mb-3 text-start">
                    <label className="text-muted small">
                      Phone Number: +{createCountry}
                      {createMobile}
                    </label>

                    <div className="input-group">
                      <div className="input-group-text p-0" style={{ zIndex: 2 }}>
                        <Select
                          options={countryOptions}
                          onChange={onCreateCountryChange}
                          filterOption={filterOption}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "none",
                              boxShadow: "none",
                              minHeight: "100%",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          className="border-0"
                          placeholder="CC"
                        />
                      </div>

                      <input
                        type="number"
                        className="form-control"
                        value={createMobile}
                        onChange={onCreatePhoneChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-floating mb-3">
                    <select
                      className="form-select"
                      value={createStatus}
                      onChange={(e) => {
                        setCreateStatus(e.target.value);
                        setCreateClosedAmount("");
                      }}
                      required
                    >
                      <option value=""></option>
                      <option value="No Status">No Status</option>
                      <option value="Follow Up">Follow Up</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Booking">Booking</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <label>Status</label>
                  </div>

                  {createStatus === "Closed" && (
                    <div className="mb-3">
                      <label className="small text-muted">Close Value</label>
                      <NumericFormat
                        className="form-control"
                        value={createClosedAmount}
                        thousandSeparator
                        prefix="RM "
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        onValueChange={(values) => setCreateClosedAmount(values.value)}
                        placeholder="RM 0.00"
                      />
                    </div>
                  )}

                  <div className="form-floating mb-3">
                    <textarea
                      className="form-control"
                      style={{ height: 110 }}
                      value={createRemarks}
                      onChange={(e) => setCreateRemarks(e.target.value)}
                    />
                    <label>Remarks</label>
                  </div>

                  <div className="mb-3 text-start">
                    <label className="form-label small text-muted">Follow Up Date</label>
                    <DateTimePicker
                        value={createFollowUp}
                        onChange={(dateObj) => handleFollowUpChange(dateObj, "create")}
                        minDate={new Date()}
                    />
                    </div>


                  <div className="d-grid gap-2 mt-5">
                    <button className="btn btn-success" type="submit">
                      Create Lead
                    </button>
                    <button className="btn btn-light" type="button" onClick={onClose}>
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {/* EDIT */}
              {isEdit && (
                <form onSubmit={onEditSubmit}>
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                    <label>Name</label>
                  </div>

                  <div className="mb-3 text-start">
                    <label className="text-muted small">
                      Phone Number: +{safeEditCountry?.value || ""}{editMobile}
                    </label>
                    <div className="input-group">
                      <div className="input-group-text p-0" style={{ zIndex: 2 }}>
                        <Select
                          options={countryOptions}
                          onChange={(opt) => setEditCountry(opt)}
                          filterOption={filterOption}
                          value={safeEditCountry}
                          menuPortalTarget={document.body}
                          styles={{
                            control: (base) => ({
                              ...base,
                              border: "none",
                              boxShadow: "none",
                              minHeight: "100%",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                          className="border-0"
                        />
                      </div>

                      <input
                        type="number"
                        className="form-control"
                        value={editMobile}
                        onChange={onEditPhoneChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-floating mb-3">
                    <select
                      className="form-select"
                      value={editStatus}
                      onChange={(e) => {
                        setEditStatus(e.target.value);
                        setEditClosedAmount("");
                      }}
                      required
                    >
                      <option value="No Status">No Status</option>
                      <option value="Follow Up">Follow Up</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Booking">Booking</option>
                      <option value="Closed">Closed</option>
                    </select>
                    <label>Status</label>
                  </div>

                  {editStatus === "Closed" &&
                  (!initialClosedAmount.current ||
                    parseFloat(initialClosedAmount.current) < 1) ? (
                    <div className="mb-3">
                      <label className="small text-muted">Closed Value</label>
                      <NumericFormat
                        className="form-control"
                        value={editClosedAmount}
                        thousandSeparator
                        prefix="RM "
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        onValueChange={(values) => setEditClosedAmount(values.value)}
                        placeholder="RM 0.00"
                      />
                    </div>
                  ) : editStatus === "Closed" ? (
                    <div className="mb-3">
                      <label className="small text-muted">Total Closed Value</label>
                      <NumericFormat
                        className="form-control"
                        value={totalClosedValue}
                        thousandSeparator
                        prefix="RM "
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        disabled
                      />
                    </div>
                  ) : null}

                  <div className="form-floating mb-3">
                    <textarea
                      className="form-control"
                      style={{ height: 110 }}
                      value={editRemarks}
                      onChange={(e) => setEditRemarks(e.target.value)}
                    />
                    <label>Remarks</label>
                  </div>

                  <div className="mb-2 text-start">
                    <label className="form-label small text-muted">Follow Up Date</label>

                    <DateTimePicker
                        value={editFollowUpDateObj}
                        onChange={(dateObj) => handleFollowUpChange(dateObj, "edit")}
                        minDate={new Date()} // ✅ tak bagi pilih tarikh lepas
                    />

                    {isFutureFollowUp && (
                        <div className="small mt-2 text-primary">
                        Scheduled:{" "}
                        <strong>
                            {format(editFollowUpDateObj, "dd MMM yyyy, h:mm a")}
                        </strong>{" "}
                        <span style={{ marginLeft: 6 }}>
                            ({formatDistanceToNow(editFollowUpDateObj, { addSuffix: true })})
                        </span>
                        </div>
                    )}
                    </div>


                  {editAddedDate && (
                    <div className="mb-3 mt-4 text-start">
                      <div className="small">
                        Added Date:{" "}
                        <strong>
                          {format(new Date(editAddedDate), "dd MMM yyyy, h:mm a")}
                          <span style={{ color: "#6c757d", marginLeft: 8 }}>
                            (
                            {formatDistanceToNow(new Date(editAddedDate), {
                              addSuffix: true,
                            })}
                            )
                          </span>
                        </strong>
                      </div>
                    </div>
                  )}

                  <div className="d-grid gap-2 mt-5">
                    <button className="btn btn-primary" type="submit">
                      Save Changes
                    </button>
                    <button className="btn btn-light" type="button" onClick={onClose}>
                      Close
                    </button>
                  </div>
                </form>
              )}

              {/* REPEAT */}
              {isRepeat && (
                <>
                  <div className="alert alert-secondary small">
                    <strong>Tip:</strong> Repeat sale only for leads with status{" "}
                    <strong>Closed</strong>.
                  </div>

                  <ul className="nav nav-tabs mb-3">
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeRepeatTab === "form" ? "active" : ""}`}
                        onClick={() => setActiveRepeatTab("form")}
                        type="button"
                      >
                        Repeat Sale
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`nav-link ${activeRepeatTab === "history" ? "active" : ""}`}
                        onClick={() => setActiveRepeatTab("history")}
                        type="button"
                      >
                        Sale History
                      </button>
                    </li>
                  </ul>

                  {activeRepeatTab === "form" && (
                    <form onSubmit={onRepeatSubmit}>
                      <div className="mb-3">
                        <label className="text-muted small">Amount</label>
                        <NumericFormat
                          className="form-control"
                          value={repeatSalesAmt}
                          thousandSeparator
                          prefix="RM "
                          allowNegative={false}
                          decimalScale={2}
                          fixedDecimalScale
                          onValueChange={(values) => setRepeatSalesAmt(values.value)}
                          placeholder="RM 0.00"
                          required
                        />
                      </div>

                      <div className="form-floating mb-3">
                        <textarea
                          className="form-control"
                          style={{ height: 110 }}
                          value={repeatRemark}
                          onChange={(e) => setRepeatRemark(e.target.value)}
                        />
                        <label>Remarks</label>
                      </div>

                      <div className="d-grid gap-2">
                        <button className="btn btn-success" type="submit">
                          Submit Repeat Sale
                        </button>
                        <button className="btn btn-light" type="button" onClick={onClose}>
                          Close
                        </button>
                      </div>
                    </form>
                  )}

                  {activeRepeatTab === "history" && (
                    <div className="table-responsive" style={{ fontSize: "0.85rem" }}>
                      {salesHistory.length === 0 ? (
                        <p className="text-muted">No repeat sales recorded yet.</p>
                      ) : (
                        <table className="table table-bordered table-sm">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Amount (RM)</th>
                              <th>Remarks</th>
                              <th>Date</th>
                              <th style={{ width: 90 }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {salesHistory.map((sale, index) => (
                              <tr key={sale.id}>
                                <td>{index + 1}</td>

                                <td style={{ minWidth: 130 }}>
                                  {editingRowId === sale.id ? (
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={editRowAmount}
                                      onChange={(e) => setEditRowAmount(e.target.value)}
                                    />
                                  ) : (
                                    parseFloat(sale.repeatAmount).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                    })
                                  )}
                                </td>

                                <td style={{ minWidth: 180 }}>
                                  {editingRowId === sale.id ? (
                                    <textarea
                                      className="form-control form-control-sm"
                                      value={editRowRemarks}
                                      onChange={(e) => setEditRowRemarks(e.target.value)}
                                    />
                                  ) : (
                                    sale.remarks || "-"
                                  )}
                                </td>

                                <td style={{ minWidth: 180 }}>
                                  {new Date(sale.createdAt).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}
                                </td>

                                <td className="align-middle">
                                  {editingRowId === sale.id ? (
                                    <div className="d-flex gap-2 justify-content-center">
                                      <button
                                        className="btn btn-sm btn-success"
                                        type="button"
                                        onClick={() => onSaveRepeatHistoryRow(sale.id)}
                                      >
                                        Save
                                      </button>
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        type="button"
                                        onClick={() => setEditingRowId(null)}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      type="button"
                                      onClick={() => {
                                        setEditingRowId(sale.id);
                                        setEditRowAmount(sale.repeatAmount);
                                        setEditRowRemarks(sale.remarks || "");
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="offcanvas-footer border-top p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              {isCreate ? (
                <span>
                  Create Single Lead
                </span>
              ) : (
                <span>Lead ID: {editId || "-"}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
